import {
  useCallback,
  useEffect,
  useRef,
  useState,
  PropsWithChildren,
  HTMLAttributes,
} from "react"
import cx from "classnames"
import { css } from "@emotion/react"
import { Icon } from "source/shared/components"

type HorizontalItemScrollerProps = PropsWithChildren<{
  scrollDistance?: number
  className?: string
}>

const DEFAULT_SCROLL_DISTANCE = 150

export function HorizontalItemScroller({
  scrollDistance = DEFAULT_SCROLL_DISTANCE,
  className,
  children,
  ...props
}: HorizontalItemScrollerProps & HTMLAttributes<HTMLDivElement>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    // round to avoid sub-pixel issues
    const scrollLeft = Math.round(el.scrollLeft)
    const maxScrollLeft = Math.round(el.scrollWidth - el.clientWidth)

    const TOLERANCE = 2
    setCanScrollLeft(scrollLeft > TOLERANCE)
    setCanScrollRight(scrollLeft < maxScrollLeft - TOLERANCE)
  }, [])

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -scrollDistance })
  }

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: scrollDistance })
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    updateScrollState()

    const observer = new ResizeObserver(updateScrollState)
    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [updateScrollState])

  return (
    <div
      className={cx("p-r", className)}
      css={styles.wrapper}
      data-overflow-left={canScrollLeft}
      data-overflow-right={canScrollRight}
    >
      {canScrollLeft && (
        <button
          type="button"
          className="text-btn circle"
          css={[styles.scrollButton, styles.leftButton]}
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          <Icon symbol="general#left-chevron" aria-hidden="true" />
        </button>
      )}

      <div
        ref={scrollRef}
        css={styles.scrollContainer}
        onScroll={updateScrollState}
        {...props}
      >
        {children}
      </div>

      {canScrollRight && (
        <button
          type="button"
          className="text-btn circle"
          css={[styles.scrollButton, styles.rightButton]}
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <Icon symbol="general#right-chevron" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

const styles = {
  wrapper: css`
    position: relative;

    // edge fade overlays
    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      width: 6rem;
      pointer-events: none;
      z-index: 1;
    }

    &[data-overflow-left="true"]::before {
      left: 0;
      background: linear-gradient(to right, var(--color-tint10), transparent);
    }

    &[data-overflow-right="true"]::after {
      right: 0;
      background: linear-gradient(to left, var(--color-tint10), transparent);
    }
  `,
  scrollContainer: css`
    display: flex;
    gap: 0.5rem;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    scroll-snap-type: x proximity;
    touch-action: manipulation;

    > * {
      scroll-snap-align: start;

      &:last-child {
        scroll-snap-align: end;
      }
    }
  `,
  scrollButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 2rem;
    height: 2rem;
    background: var(--color-tint10);
    font-size: 0.875rem;
    z-index: 2;
  `,
  leftButton: css`
    left: -0.75rem;
  `,
  rightButton: css`
    right: -0.75rem;
  `,
}
